import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Bell, Check, CheckCheck, AlertTriangle, Clock, FileText, Info, RefreshCw, Loader2 } from 'lucide-react';
import { formatDateTime } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Notificaciones = () => {
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [notificaciones, setNotificaciones] = useState([]);

    useEffect(() => {
        fetchNotificaciones();
    }, []);

    const fetchNotificaciones = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/api/notificaciones`);
            setNotificaciones(response.data);
        } catch (error) {
            console.error('Error fetching notificaciones:', error);
            toast.error('Error al cargar notificaciones');
        } finally {
            setLoading(false);
        }
    };

    const handleMarkAsRead = async (id) => {
        try {
            await axios.put(`${API_URL}/api/notificaciones/${id}`, { leida: true });
            fetchNotificaciones();
        } catch (error) {
            console.error('Error marking as read:', error);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            await axios.post(`${API_URL}/api/notificaciones/mark-all-read`);
            toast.success('Todas las notificaciones marcadas como leídas');
            fetchNotificaciones();
        } catch (error) {
            console.error('Error marking all as read:', error);
            toast.error('Error al marcar notificaciones');
        }
    };

    const handleGenerateAlerts = async () => {
        setGenerating(true);
        try {
            const response = await axios.post(`${API_URL}/api/notificaciones/generar-alertas`);
            const { alertas_creadas, servicios_prox_vencer, servicios_vencidos } = response.data;
            
            if (alertas_creadas > 0) {
                toast.success(`Se generaron ${alertas_creadas} nuevas alertas`);
            } else {
                toast.info('No hay nuevas alertas para generar');
            }
            
            fetchNotificaciones();
        } catch (error) {
            console.error('Error generating alerts:', error);
            toast.error('Error al generar alertas');
        } finally {
            setGenerating(false);
        }
    };

    const getTipoIcon = (tipo) => {
        switch (tipo) {
            case 'alerta_vencimiento':
            case 'alerta_vencido':
                return <Clock className="text-amber-500" size={20} />;
            case 'alerta_mora':
            case 'suspension_servicio':
                return <AlertTriangle className="text-rose-500" size={20} />;
            case 'alerta_facturacion':
            case 'alerta_edp_pendiente':
                return <FileText className="text-[#5B9A9A]" size={20} />;
            default:
                return <Info className="text-slate-400" size={20} />;
        }
    };

    const getTipoBadge = (tipo) => {
        const tipos = {
            alerta_vencimiento: { label: 'Vencimiento', className: 'bg-amber-100 text-amber-700 border-amber-200' },
            alerta_vencido: { label: 'Vencido', className: 'bg-red-100 text-red-700 border-red-200' },
            alerta_mora: { label: 'Mora', className: 'bg-rose-100 text-rose-700 border-rose-200' },
            alerta_edp_pendiente: { label: 'EDP Pendiente', className: 'bg-[#D5E8E8] text-[#3D7A7A] border-[#A5D0D0]' },
            alerta_edp_sin_respuesta: { label: 'EDP Sin Respuesta', className: 'bg-purple-100 text-purple-700 border-purple-200' },
            alerta_facturacion: { label: 'Facturación', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
            compromiso_incumplido: { label: 'Compromiso', className: 'bg-orange-100 text-orange-700 border-orange-200' },
            suspension_servicio: { label: 'Suspensión', className: 'bg-slate-100 text-slate-700 border-slate-200' },
            sistema: { label: 'Sistema', className: 'bg-gray-100 text-gray-700 border-gray-200' }
        };
        return tipos[tipo] || { label: tipo, className: 'bg-gray-100 text-gray-700 border-gray-200' };
    };

    const unreadCount = notificaciones.filter(n => !n.leida).length;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Notificaciones</h1>
                    <p className="text-slate-500 text-sm mt-1">
                        {unreadCount > 0 ? `${unreadCount} notificaciones sin leer` : 'Todas las notificaciones leídas'}
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button 
                        variant="outline" 
                        onClick={handleGenerateAlerts}
                        disabled={generating}
                        data-testid="generate-alerts-btn"
                    >
                        {generating ? (
                            <Loader2 size={16} className="mr-2 animate-spin" />
                        ) : (
                            <RefreshCw size={16} className="mr-2" />
                        )}
                        Generar Alertas
                    </Button>
                    {unreadCount > 0 && (
                        <Button variant="outline" onClick={handleMarkAllAsRead}>
                            <CheckCheck size={16} className="mr-2" />
                            Marcar todas como leídas
                        </Button>
                    )}
                </div>
            </div>

            {/* Notifications List */}
            <Card>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex items-center justify-center h-48">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5B9A9A]"></div>
                        </div>
                    ) : notificaciones.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-48 text-slate-500">
                            <Bell size={48} className="text-slate-300 mb-2" />
                            <p>No hay notificaciones</p>
                        </div>
                    ) : (
                        <div className="divide-y">
                            {notificaciones.map((notif) => {
                                const tipoBadge = getTipoBadge(notif.tipo);
                                return (
                                    <div 
                                        key={notif.id} 
                                        className={`p-4 hover:bg-slate-50 transition-colors ${
                                            !notif.leida ? 'bg-[#E8F4F4]/50' : ''
                                        }`}
                                    >
                                        <div className="flex items-start gap-4">
                                            <div className="mt-0.5">
                                                {getTipoIcon(notif.tipo)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between gap-2">
                                                    <div>
                                                        <p className={`font-medium ${!notif.leida ? 'text-slate-900' : 'text-slate-600'}`}>
                                                            {notif.titulo}
                                                        </p>
                                                        <p className="text-sm text-slate-500 mt-1">
                                                            {notif.mensaje}
                                                        </p>
                                                    </div>
                                                    <Badge variant="outline" className={`${tipoBadge.className} border`}>
                                                        {tipoBadge.label}
                                                    </Badge>
                                                </div>
                                                <div className="flex items-center justify-between mt-2">
                                                    <p className="text-xs text-slate-400">
                                                        {formatDateTime(notif.created_at)}
                                                    </p>
                                                    {!notif.leida && (
                                                        <Button 
                                                            size="sm" 
                                                            variant="ghost"
                                                            onClick={() => handleMarkAsRead(notif.id)}
                                                        >
                                                            <Check size={14} className="mr-1" />
                                                            Marcar como leída
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default Notificaciones;
